import { useState } from "react";
import { ethers } from "ethers";
import "./App.css";

const COVER_POOL_ADDRESS = "0x5DF8635ffeEc02176284ac5994eDA7A3560bAB8D";
const COVER_POLICY_ADDRESS = "0x50Bcb7999620c835B28d92E1a949160169C3be00";
const API_URL = "https://api.shieldfi.tech";

const COVER_POOL_ABI = [
  "function depositLiquidity() public payable",
  "function withdrawLiquidity(uint _amount) public",
  "function getPoolBalance() public view returns (uint)",
  "function balances(address) public view returns (uint)",
];

const COVER_POLICY_ABI = [
  "function createPolicy(address _protocol, uint _coverAmount) public payable",
  "function calculatePremium(uint _coverAmount) public pure returns (uint)",
  "function getPoliciesCount() public view returns (uint)",
  "function getUserPolicies(address _user) public view returns (uint[])",
  "function getPolicy(uint _policyId) public view returns (tuple(uint id, address buyer, address protocol, uint coverAmount, uint premium, uint startTime, uint expiry, bool active, bool claimed))",
];

function App() {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [poolBalance, setPoolBalance] = useState("0");
  const [myBalance, setMyBalance] = useState("0");
  const [depositAmount, setDepositAmount] = useState("");
  const [coverAmount, setCoverAmount] = useState("");
  const [protocolAddress, setProtocolAddress] = useState("");
  const [riskData, setRiskData] = useState(null);
  const [loadingRisk, setLoadingRisk] = useState(false);
  const [tab, setTab] = useState("lp");
  const [status, setStatus] = useState("");
  const [policies, setPolicies] = useState([]);

  const connectWallet = async () => {
    if (!window.ethereum) { setStatus("MetaMask non trovato."); return; }
    try {
      const _provider = new ethers.providers.Web3Provider(window.ethereum);
      await _provider.send("eth_requestAccounts", []);
      const network = await _provider.getNetwork();
      if (network.chainId !== 84532) {
        setStatus("Cambia rete su MetaMask — seleziona Base Sepolia Testnet");
        return;
      }
      const _signer = _provider.getSigner();
      const _account = await _signer.getAddress();
      setProvider(_provider);
      setSigner(_signer);
      setAccount(_account);
      setStatus("Wallet connesso: " + _account.slice(0, 6) + "..." + _account.slice(-4));
      await loadData(_provider, _signer, _account);
    } catch (e) { setStatus("Errore: " + e.message); }
  };

  const loadData = async (_provider, _signer, _account) => {
    try {
      const pool = new ethers.Contract(COVER_POOL_ADDRESS, COVER_POOL_ABI, _provider);
      const balance = await pool.getPoolBalance();
      const myBal = await pool.balances(_account);
      setPoolBalance(ethers.utils.formatEther(balance));
      setMyBalance(ethers.utils.formatEther(myBal));
      const policy = new ethers.Contract(COVER_POLICY_ADDRESS, COVER_POLICY_ABI, _provider);
      const userPolicyIds = await policy.getUserPolicies(_account);
      const userPolicies = await Promise.all(userPolicyIds.map(id => policy.getPolicy(id)));
      setPolicies(userPolicies);
    } catch (e) { setStatus("Errore caricamento: " + e.message); }
  };

  const fetchRiskScore = async (address) => {
    if (!address || !address.startsWith("0x") || address.length !== 42) return;
    setLoadingRisk(true);
    setRiskData(null);
    try {
      const res = await fetch(`${API_URL}/api/premium/${address}/${coverAmount || 1}`);
      const data = await res.json();
      setRiskData(data);
    } catch (e) {
      setRiskData({ error: "Errore nel calcolo del rischio" });
    }
    setLoadingRisk(false);
  };

  const deposit = async () => {
    if (!signer || !depositAmount) return;
    try {
      setStatus("Deposito in corso...");
      const pool = new ethers.Contract(COVER_POOL_ADDRESS, COVER_POOL_ABI, signer);
      const tx = await pool.depositLiquidity({ value: ethers.utils.parseEther(depositAmount) });
      await tx.wait();
      setStatus("Deposito completato!");
      await loadData(provider, signer, account);
    } catch (e) { setStatus("Errore: " + e.message); }
  };

  const buyCoverage = async () => {
    if (!signer || !coverAmount || !protocolAddress) return;
    try {
      setStatus("Acquisto copertura in corso...");
      const policy = new ethers.Contract(COVER_POLICY_ADDRESS, COVER_POLICY_ABI, signer);
      const p = await policy.calculatePremium(ethers.utils.parseEther(coverAmount));
      const tx = await policy.createPolicy(protocolAddress, ethers.utils.parseEther(coverAmount), { value: p });
      await tx.wait();
      setStatus("Copertura acquistata!");
      await loadData(provider, signer, account);
    } catch (e) { setStatus("Errore: " + e.message); }
  };

  const riskColor = (level) => level === "LOW" ? "#10B981" : level === "MEDIUM" ? "#F59E0B" : "#EF4444";

  return (
    <div style={{ fontFamily: "sans-serif", maxWidth: 640, margin: "40px auto", padding: 20 }}>
      <h1 style={{ color: "#1E3A5F" }}>🛡️ DeFi Shield Protocol</h1>
      <p style={{ color: "#475569" }}>AI-powered DeFi insurance on Base Sepolia</p>

      {!account ? (
        <button onClick={connectWallet} style={btnStyle("#2563EB")}>Connetti MetaMask</button>
      ) : (
        <p style={{ color: "#10B981" }}>✅ {account.slice(0, 6)}...{account.slice(-4)}</p>
      )}

      {status && <p style={{ color: "#F59E0B", marginTop: 10 }}>{status}</p>}

      <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
        <button onClick={() => setTab("lp")} style={btnStyle(tab === "lp" ? "#1E3A5F" : "#CBD5E1")}>💰 Liquidity Provider</button>
        <button onClick={() => setTab("cover")} style={btnStyle(tab === "cover" ? "#1E3A5F" : "#CBD5E1")}>🛡️ Compra Copertura</button>
        <button onClick={() => setTab("policies")} style={btnStyle(tab === "policies" ? "#1E3A5F" : "#CBD5E1")}>📋 Le mie Polizze</button>
      </div>

      <div style={{ marginTop: 20, padding: 20, background: "#F1F5F9", borderRadius: 8 }}>
        <p><strong>Pool Balance:</strong> {poolBalance} ETH</p>
        <p><strong>Il tuo deposito:</strong> {myBalance} ETH</p>
      </div>

      {tab === "lp" && (
        <div style={{ marginTop: 20 }}>
          <h2>Deposita Liquidità</h2>
          <p style={{ color: "#475569" }}>Deposita ETH nel pool e guadagna una quota dei premi pagati dagli assicurati.</p>
          <input type="number" placeholder="Importo ETH" value={depositAmount}
            onChange={e => setDepositAmount(e.target.value)} style={inputStyle} />
          <button onClick={deposit} style={btnStyle("#10B981")}>Deposita</button>
        </div>
      )}

      {tab === "cover" && (
        <div style={{ marginTop: 20 }}>
          <h2>Compra Copertura</h2>
          <input type="text" placeholder="Indirizzo protocollo da coprire (0x...)"
            value={protocolAddress}
            onChange={e => { setProtocolAddress(e.target.value); fetchRiskScore(e.target.value); }}
            style={inputStyle} />
          <input type="number" placeholder="Importo da coprire (ETH)" value={coverAmount}
            onChange={e => { setCoverAmount(e.target.value); if (protocolAddress) fetchRiskScore(protocolAddress); }}
            style={inputStyle} />

          {loadingRisk && <p style={{ color: "#475569" }}>⏳ Calcolo risk score in corso...</p>}

          {riskData && !riskData.error && (
            <div style={{ background: "#F1F5F9", padding: 15, borderRadius: 8, marginBottom: 15 }}>
              <p><strong>Risk Level:</strong> <span style={{ color: riskColor(riskData.riskLevel), fontWeight: "bold" }}>{riskData.riskLevel}</span></p>
              <p><strong>Risk Multiplier:</strong> {riskData.riskMultiplier}x</p>
              <p><strong>Premio annuale:</strong> {riskData.premiumRate * 100}% → <strong>{riskData.premium} ETH</strong></p>
            </div>
          )}

          {riskData && riskData.error && (
            <p style={{ color: "#EF4444" }}>{riskData.error}</p>
          )}

          <button onClick={buyCoverage} style={btnStyle("#2563EB")}>Acquista Copertura</button>
        </div>
      )}

      {tab === "policies" && (
        <div style={{ marginTop: 20 }}>
          <h2>Le mie Polizze</h2>
          {policies.length === 0 ? (
            <p style={{ color: "#475569" }}>Nessuna polizza attiva.</p>
          ) : (
            policies.map((p, i) => (
              <div key={i} style={{ background: "white", padding: 15, borderRadius: 8, marginBottom: 10, border: "1px solid #E2E8F0" }}>
                <p><strong>Protocollo:</strong> {p.protocol.slice(0, 10)}...</p>
                <p><strong>Copertura:</strong> {ethers.utils.formatEther(p.coverAmount)} ETH</p>
                <p><strong>Premio:</strong> {ethers.utils.formatEther(p.premium)} ETH</p>
                <p><strong>Stato:</strong> {p.active ? "✅ Attiva" : "❌ Non attiva"}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const btnStyle = (bg) => ({
  background: bg, color: "white", border: "none",
  padding: "10px 20px", borderRadius: 6, cursor: "pointer", fontWeight: "bold",
});

const inputStyle = {
  display: "block", width: "100%", padding: "10px", marginBottom: 10,
  borderRadius: 6, border: "1px solid #CBD5E1", fontSize: 14,
};

export default App;
